import { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  type: string;
  role: string | null;
  mentions: number;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  source: string | GraphNode;
  target: string | GraphNode;
  type: string;
}

interface Props {
  data: { nodes: GraphNode[]; edges: GraphLink[] };
  height?: number;
}

// Internal viewBox dimensions — SVG scales via CSS width 100%
const VB_WIDTH = 1400;

const TYPE_COLOR: Record<string, string> = {
  PERSON:       '#a52f4a',
  ORGANIZATION: '#2d4f82',
  LOCATION:     '#c98a3b',
  OBJECT:       '#5f5043',
  MONEY:        '#5a6b1e',
  EVENT:        '#862c2c',
};

export default function EntityGraph({ data, height = 620 }: Props) {
  const width = VB_WIDTH;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  // Compute connected set when selected changes
  const { connected, connectedEdges } = useMemo(() => {
    if (!selected) return { connected: null, connectedEdges: null };
    const conn = new Set<string>([selected]);
    const edgeKeys = new Set<string>();
    data.edges.forEach((e, i) => {
      const s = typeof e.source === 'string' ? e.source : e.source.id;
      const t = typeof e.target === 'string' ? e.target : e.target.id;
      if (s === selected || t === selected) {
        conn.add(s); conn.add(t);
        edgeKeys.add(String(i));
      }
    });
    return { connected: conn, connectedEdges: edgeKeys };
  }, [selected, data.edges]);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Clone data (d3 mutates positions)
    const nodes: GraphNode[] = data.nodes.map((n) => ({ ...n }));
    const links: GraphLink[] = data.edges.map((e) => ({ ...e }));

    const maxMentions = Math.max(1, ...nodes.map((n) => n.mentions));

    // Defs (arrow markers)
    const defs = svg.append('defs');
    defs.append('marker')
      .attr('id', 'arrow-graph')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 10)
      .attr('refY', 5)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', 'M0,0L10,5L0,10z')
      .attr('fill', '#b08a3c');

    // Background click clears selection — semi-transparent paper
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'rgba(253, 250, 243, 0.35)')
      .attr('stroke', '#c9bda8')
      .attr('stroke-width', 1)
      .on('click', () => setSelected(null));

    // Force simulation
    const sim = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links).id((d) => d.id).distance(110).strength(0.5))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collide', d3.forceCollide<GraphNode>().radius((d) => 8 + 12 * (d.mentions / maxMentions) + 6));

    // Container groups
    const linkGroup = svg.append('g').attr('class', 'links');
    const linkLabelGroup = svg.append('g').attr('class', 'link-labels');
    const nodeGroup = svg.append('g').attr('class', 'nodes');

    const link = linkGroup.selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#b08a3c')
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 1.4)
      .attr('marker-end', 'url(#arrow-graph)');

    const linkLabel = linkLabelGroup.selectAll<SVGTextElement, GraphLink>('text')
      .data(links)
      .join('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('font-family', 'IBM Plex Mono, monospace')
      .attr('font-weight', 600)
      .attr('fill', '#3a2d1f')
      .attr('paint-order', 'stroke')
      .attr('stroke', '#fdfaf3')
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('pointer-events', 'none')
      .text((d) => (d.type || '').replace(/_/g, ' ').toLowerCase().slice(0, 20));

    const node = nodeGroup.selectAll<SVGGElement, GraphNode>('g')
      .data(nodes)
      .join('g')
      .style('cursor', 'grab')
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelected((cur) => (cur === d.id ? null : d.id));
      });

    node.append('circle')
      .attr('r', (d) => 8 + 12 * (d.mentions / maxMentions))
      .attr('fill', (d) => TYPE_COLOR[d.type] || '#666')
      .attr('fill-opacity', 0.88)
      .attr('stroke', '#1a1a1a')
      .attr('stroke-width', 1.2);

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => 8 + 12 * (d.mentions / maxMentions) + 12)
      .attr('font-size', 10.5)
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-weight', 600)
      .attr('fill', '#1a1a1a')
      .attr('pointer-events', 'none')
      .text((d) => d.label.length > 24 ? d.label.slice(0, 23) + '…' : d.label);

    // Drag behavior
    const drag = d3.drag<SVGGElement, GraphNode>()
      .on('start', (event, d) => {
        if (!event.active) sim.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
        d3.select(event.sourceEvent.currentTarget).style('cursor', 'grabbing');
      })
      .on('drag', (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event, d) => {
        if (!event.active) sim.alphaTarget(0);
        // Keep position fixed after drag (set to null to release)
        // d.fx = null; d.fy = null;
        d3.select(event.sourceEvent.currentTarget).style('cursor', 'grab');
      });
    node.call(drag);

    sim.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as GraphNode).x || 0)
        .attr('y1', (d) => (d.source as GraphNode).y || 0)
        .attr('x2', (d) => (d.target as GraphNode).x || 0)
        .attr('y2', (d) => (d.target as GraphNode).y || 0);
      linkLabel
        .attr('x', (d) => (((d.source as GraphNode).x || 0) + ((d.target as GraphNode).x || 0)) / 2)
        .attr('y', (d) => (((d.source as GraphNode).y || 0) + ((d.target as GraphNode).y || 0)) / 2);
      node.attr('transform', (d) => `translate(${d.x || 0}, ${d.y || 0})`);
    });

    return () => {
      sim.stop();
    };
  }, [data, width, height]);

  // Apply highlight via opacity (separate effect — doesn't re-run sim)
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll<SVGGElement, GraphNode>('.nodes g').each(function (d) {
      const dim = !!connected && !connected.has(d.id);
      d3.select(this).attr('opacity', dim ? 0.15 : 1);
      d3.select(this).select('circle')
        .attr('stroke', selected === d.id ? '#b08a3c' : '#1a1a1a')
        .attr('stroke-width', selected === d.id ? 3 : 1.2);
    });
    svg.selectAll<SVGLineElement, GraphLink>('.links line').each(function (_d, i) {
      const dim = !!connectedEdges && !connectedEdges.has(String(i));
      d3.select(this)
        .attr('stroke-opacity', dim ? 0.08 : 0.85)
        .attr('stroke', connectedEdges?.has(String(i)) ? '#b08a3c' : '#b08a3c')
        .attr('stroke-width', connectedEdges?.has(String(i)) ? 2 : 1.4);
    });
    svg.selectAll<SVGTextElement, GraphLink>('.link-labels text').each(function (_d, i) {
      const dim = !!connectedEdges && !connectedEdges.has(String(i));
      d3.select(this).attr('opacity', dim ? 0.1 : 1);
    });
  }, [connected, connectedEdges, selected]);

  return (
    <div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: 'auto', userSelect: 'none' }}
      />
      {/* Legend */}
      <div className="mt-3 flex gap-3 flex-wrap font-mono text-[10px] uppercase tracking-wider">
        {Object.entries(TYPE_COLOR).map(([t, c]) => (
          <span key={t} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block border border-ink" style={{ background: c }} />
            <span className="text-ink-2">{t}</span>
          </span>
        ))}
        {selected && (
          <span className="ml-auto text-brass">
            Click node lain atau background untuk clear highlight
          </span>
        )}
      </div>
    </div>
  );
}
